import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay, subDays, differenceInMinutes } from "date-fns";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

interface DashboardStats {
  totalDischargesToday: number;
  totalDischargesYesterday: number;
  dischargeInitiatedToday: number;
  avgDischargeTime: string;
  pendingDischargesToday: number;
  pendingDischargesYesterday: number;
  billingPending: number;
  paymentPending: number;
  dischargeTrend: number;
  pendingTrend: number;
}

export const useDashboardStats = () => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ["dashboard-stats", organizationId, currentBranchId],
    queryFn: async (): Promise<DashboardStats> => {
      if (currentBranchId == null || !organizationId) {
        return { totalDischargesToday: 0, totalDischargesYesterday: 0, dischargeInitiatedToday: 0, avgDischargeTime: "0h 0m", pendingDischargesToday: 0, pendingDischargesYesterday: 0, billingPending: 0, paymentPending: 0, dischargeTrend: 0, pendingTrend: 0 };
      }
      const orgId = organizationId;
      const today = new Date();
      const todayStart = startOfDay(today).toISOString();
      const todayEnd = endOfDay(today).toISOString();
      const yesterday = subDays(today, 1);
      const yesterdayStart = startOfDay(yesterday).toISOString();
      const yesterdayEnd = endOfDay(yesterday).toISOString();

      // 1. Total Discharges Today
      const { data: dischargedToday, error: e1 } = await supabase
        .from("activities").select("id, patient_id, completion_time")
        .eq("workflow_step", "PD").eq("status", "completed")
        .eq("branch_id", currentBranchId).eq("organization_id", orgId)
        .gte("completion_time", todayStart).lte("completion_time", todayEnd);
      if (e1) throw e1;
      const totalDischargesToday = dischargedToday?.length || 0;

      // 1b. Discharge Initiated Today
      const { data: initiatedToday, error: e2 } = await supabase
        .from("activities").select("id")
        .eq("workflow_step", "MD").eq("status", "completed")
        .eq("branch_id", currentBranchId).eq("organization_id", orgId)
        .gte("completion_time", todayStart).lte("completion_time", todayEnd);
      if (e2) throw e2;
      const dischargeInitiatedToday = initiatedToday?.length || 0;

      // 2. Avg Discharge Time
      let avgDischargeTime = "0h 0m";
      if (dischargedToday && dischargedToday.length > 0) {
        const patientIds = dischargedToday.map(d => d.patient_id);
        const { data: mdActivities } = await supabase
          .from("activities").select("patient_id, started_at")
          .eq("workflow_step", "MD").eq("branch_id", currentBranchId).eq("organization_id", orgId)
          .in("patient_id", patientIds);
        if (mdActivities && mdActivities.length > 0) {
          const mdMap = new Map(mdActivities.map(md => [md.patient_id, md.started_at]));
          let totalMinutes = 0, validCount = 0;
          dischargedToday.forEach(pd => {
            const mdStartTime = mdMap.get(pd.patient_id);
            if (mdStartTime && pd.completion_time) {
              const minutes = differenceInMinutes(new Date(pd.completion_time), new Date(mdStartTime as string));
              if (minutes > 0) { totalMinutes += minutes; validCount++; }
            }
          });
          if (validCount > 0) {
            const avgMinutes = Math.round(totalMinutes / validCount);
            avgDischargeTime = `${Math.floor(avgMinutes / 60)}h ${avgMinutes % 60}m`;
          }
        }
      }

      // 3. Pending Discharges
      const { data: pendingPatients, error: e3 } = await supabase
        .from("patients").select("id")
        .eq("initiated", true).neq("discharge_status", "discharged")
        .eq("branch_id", currentBranchId).eq("organization_id", orgId);
      if (e3) throw e3;

      const { data: mdCompletedPatients, error: e4 } = await supabase
        .from("activities").select("patient_id")
        .eq("workflow_step", "MD").eq("status", "completed")
        .eq("branch_id", currentBranchId).eq("organization_id", orgId);
      if (e4) throw e4;
      const mdCompletedSet = new Set((mdCompletedPatients || []).map(a => a.patient_id));
      const pendingDischargesToday = (pendingPatients || []).filter(p => mdCompletedSet.has(p.id)).length;

      // 4. Billing Pending
      const { data: billingActivities, error: e5 } = await supabase
        .from("activities").select("id")
        .in("workflow_step", ["BS", "BA"]).in("status", ["pending", "in-progress"])
        .eq("branch_id", currentBranchId).eq("organization_id", orgId);
      if (e5) throw e5;
      const billingPending = billingActivities?.length || 0;

      // 5. Payment Pending
      const { data: paymentActivities, error: e6 } = await supabase
        .from("activities").select("id, patient_id")
        .eq("workflow_step", "PC").in("status", ["pending", "in-progress"])
        .eq("branch_id", currentBranchId).eq("organization_id", orgId);
      if (e6) throw e6;

      const { data: mdCompletedForPC, error: e7 } = await supabase
        .from("activities").select("patient_id")
        .eq("workflow_step", "MD").eq("status", "completed")
        .eq("branch_id", currentBranchId).eq("organization_id", orgId);
      if (e7) throw e7;
      const mdCompletedForPaymentSet = new Set((mdCompletedForPC || []).map(a => a.patient_id));
      const paymentPending = (paymentActivities || []).filter(a => a.patient_id && mdCompletedForPaymentSet.has(a.patient_id)).length;

      // 6. Yesterday's discharges
      const { data: dischargedYesterday } = await supabase
        .from("activities").select("id")
        .eq("workflow_step", "PD").eq("status", "completed")
        .eq("branch_id", currentBranchId).eq("organization_id", orgId)
        .gte("completion_time", yesterdayStart).lte("completion_time", yesterdayEnd);
      const totalDischargesYesterday = dischargedYesterday?.length || 0;

      // 7. Yesterday's pending
      const { data: mdYesterday } = await supabase
        .from("activities").select("patient_id")
        .eq("workflow_step", "MD").eq("status", "completed")
        .eq("branch_id", currentBranchId).eq("organization_id", orgId)
        .gte("completion_time", yesterdayStart).lte("completion_time", yesterdayEnd);
      const pendingDischargesYesterday = Math.max(0, (mdYesterday?.length || 0) - totalDischargesYesterday);

      const dischargeTrend = totalDischargesYesterday > 0
        ? Math.round(((totalDischargesToday - totalDischargesYesterday) / totalDischargesYesterday) * 100)
        : totalDischargesToday > 0 ? 100 : 0;
      const pendingTrend = pendingDischargesYesterday > 0
        ? Math.round(((pendingDischargesToday - pendingDischargesYesterday) / pendingDischargesYesterday) * 100)
        : pendingDischargesToday > 0 ? 100 : 0;

      return { totalDischargesToday, totalDischargesYesterday, dischargeInitiatedToday, avgDischargeTime, pendingDischargesToday, pendingDischargesYesterday, billingPending, paymentPending, dischargeTrend, pendingTrend };
    },
    enabled: currentBranchId != null && !!organizationId,
    refetchInterval: 30000,
  });
};
